import React, { useState, useEffect, createContext } from "react";
import useAuth from "../hooks/useAuth";
import {
  useGetPromptsQuery,
  useGetWebPubSubQuery,
  useLazyGetContractQuery,
} from "../redux/slices/indexApiSlice";
import { ContractProgress, LoadingState } from "../types/contract";
import { WebPubSubClient } from "@azure/web-pubsub-client";

const initialState: {
  contractProgresses: ContractProgress[];
  analyzingNumber: number | null | undefined;
  isAnalyzing?: boolean;
  analyzingSuccess?: boolean;
} = {
  contractProgresses: [],
  analyzingNumber: 0,
  isAnalyzing: false,
  analyzingSuccess: false,
};

const ContractContext = createContext(initialState);

const ContractProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();

  const {
    data: dataPubSub,
    isSuccess: isSuccessPubSub,
    refetch: refetchPubSub,
  } = useGetWebPubSubQuery(isAuthenticated, {
    skip: !isAuthenticated || !user?.id,
  });

  const [getContract, { data: contract }] = useLazyGetContractQuery();
  const { data: prompts } = useGetPromptsQuery(isAuthenticated, {
    skip: !isAuthenticated || !user?.id,
  });

  const [contractProgresses, setContractProgresses] = useState<
    ContractProgress[]
  >([]);

  const [analyzingNumber, setAnalyzingNumber] = useState<number | null>();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzingSuccess, setAnalyzingSuccess] = useState(false);
  const [pubSubClient, setPubSubClient] = useState<WebPubSubClient | null>(
    null
  );

  useEffect(() => {
    if (isAuthenticated) {
      refetchPubSub();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isSuccessPubSub && dataPubSub && user.account?.id) {
      connectToPubSub(dataPubSub.token);
    }
  }, [isSuccessPubSub, dataPubSub, user]);

  useEffect(() => {
    // Update analyzing status based on contract analyze status

    if (
      contractProgresses.every(
        (c) =>
          (c.overallProgress === 100 ||
            c.loadingState === LoadingState.COMPLETED) &&
          c.documentsProgress?.every(
            (doc) =>
              doc.overallProgress === 100 ||
              doc.loadingState === LoadingState.COMPLETED
          )
      )
    ) {
      setAnalyzingSuccess(true);
      setIsAnalyzing(false);
    } else {
      setIsAnalyzing(true);
      setAnalyzingSuccess(false);
    }
  }, [contractProgresses]);

  // Cleanup PubSub client on unmount
  useEffect(() => {
    return () => {
      if (pubSubClient) {
        try {
          pubSubClient.stop();
        } catch (error) {
          console.error("Error stopping PubSub client during cleanup:", error);
        }
      }
    };
  }, [pubSubClient]);

  useEffect(() => {
    if (!isAnalyzing && analyzingNumber! > 0) {
      setIsAnalyzing(true);
    }

    if (isAnalyzing && analyzingNumber === 0) {
      setIsAnalyzing(false);
      setAnalyzingSuccess(true);
    }
  }, [analyzingNumber, isAnalyzing]);

  const connectToPubSub = async (token: string) => {
    if (pubSubClient) {
      // If client already exists, cleanup
      try {
        await pubSubClient.stop();
      } catch (err) {
        console.error("Error stopping PubSub client:", err);
      }
    }

    try {
      const client = new WebPubSubClient({
        getClientAccessUrl: async () => token,
      });

      client.on("connected", () => {
        console.log("[PubSub] Connected successfully");
      });

      client.on("disconnected", () => {
        console.log("[PubSub] Disconnected");
      });

      client.on("group-message", (e: any) => {
        const data = e.message?.data;
        if (data?.type === "contractAnalyze") {
          // refetch the actual contract

          if (data.data.contractId) {
            getContract(data.data.contractId);
          }

          // Update the contract in our list
          setContractProgresses((prevProgresses) => {
            const newProgresses = [...prevProgresses];

            const contractIndex = prevProgresses.findIndex(
              (c) => c.contractId === data.data.contractId
            );

            // change the prompt id's to prompt names
            if (data.data.loadingStatePerPrompt) {
              const newloadingStatePerPrompt: Record<string, LoadingState> = {};

              Object.entries(data.data.loadingStatePerPrompt).forEach(
                ([id, state]) => {
                  const title = prompts?.find((p) => p.id === id)?.title || id;

                  newloadingStatePerPrompt[title] =
                    data.data.loadingStatePerPrompt[id];
                }
              );

              data.data.loadingStatePerPrompt = newloadingStatePerPrompt;
            }

            if (contractIndex > -1) {
              // Update existing contract
              newProgresses[contractIndex] = {
                ...newProgresses[contractIndex],
                ...data.data,
              };
            } else if (data.data.contractId) {
              // Add new contract if we have the contract data
              newProgresses.push(data.data);
            }
            return newProgresses;
          });
        } else if (data?.type === "documentAnalyze") {
          // Handle document analysis updates if needed
          console.log("[PubSub] Document analysis update received:", data);

          setContractProgresses((prevProgresses) => {
            const newProgresses = [...prevProgresses];

            const contractIndex = prevProgresses.findIndex(
              (d) => d.contractId === data.data.contractId
            );

            // change the prompt id's to prompt names
            if (data.data.loadingStatePerPrompt) {
              const newloadingStatePerPrompt: Record<string, LoadingState> = {};

              Object.entries(data.data.loadingStatePerPrompt).forEach(
                ([id, state]) => {
                  const title = prompts?.find((p) => p.id === id)?.title || id;

                  newloadingStatePerPrompt[title] =
                    data.data.loadingStatePerPrompt[id];
                }
              );

              data.data.loadingStatePerPrompt = newloadingStatePerPrompt;
            }

            if (contractIndex > -1) {
              // Update existing contract object

              let docIndex = newProgresses[
                contractIndex
              ].documentsProgress?.findIndex(
                (doc) => doc.documentId === data.data.documentId
              );

              if (docIndex === undefined) {
                docIndex = -1; // If not found, set to -1
              }

              if (!newProgresses[contractIndex].documentsProgress) {
                newProgresses[contractIndex].documentsProgress = [data.data];
              } else if (
                newProgresses[contractIndex].documentsProgress &&
                docIndex > -1
              ) {
                newProgresses[contractIndex].documentsProgress![docIndex] =
                  data.data;
              } else {
                // If we don't have the document, add it
                newProgresses[contractIndex].documentsProgress!.push(data.data);
              }

              // if the contract's loading state is queued, we can set it to analyzing (since we have a document being extracted)
              if (
                newProgresses[contractIndex].loadingState !==
                  data.data.loadingState &&
                data.data.loadingState !== LoadingState.COMPLETED // never set the contract to completed
              ) {
                newProgresses[contractIndex].loadingState =
                  data.data.loadingState;
              }
            } else {
              // If we have a documentId but no contract, we can still add it
              newProgresses.push({
                contractId: data.data.contractId,
                name: data.data.name,
                loadingState: LoadingState.QUEUED,
                documentsProgress: [data.data],
              });
            }

            return newProgresses;
          });
        }
      });

      await client.start();
      const group = `contract-tenant-${user.account?.id}`;
      await client.joinGroup(group);

      setPubSubClient(client);
    } catch (err) {
      console.error("Error connecting to PubSub:", err);
    }
  };

  return (
    <ContractContext.Provider
      value={{
        analyzingNumber,
        isAnalyzing,
        analyzingSuccess,
        contractProgresses,
      }}
    >
      {children}
    </ContractContext.Provider>
  );
};

export { ContractContext, ContractProvider };
